// dayEnd — the end-of-day / end-of-week review, declared once on the phone
// and checked against the server that decides it (dayEndContract.test.js).
//
// ── What this module is NOT ────────────────────────────────────────────────
//
// It does not work out which jobs are finished. That is the whole point.
// server/utils/dayEnd.js builds the plan and GET /api/field/day-end serves
// it; this file only knows how to ASK for it, how to group what comes back,
// and what to call each group. A screen that decided "this one is done" from
// the list it happened to have loaded would eventually mark a job complete
// with an open task on it — which is the defect the feature exists to fix.
//
// So the only judgements here are presentational: the order the groups are
// read in, and the words above each one.
//
// ── Why the scopes are a table ─────────────────────────────────────────────
//
// `scope` is validated by the server (`body('scope').isIn(SCOPES)`), so a
// third one invented on the phone is a 400 with nothing on screen explaining
// it. The table is the same two values, and the contract suite reads the
// server's own list and compares.

/** The two reviews the server accepts. */
export const DAY_END_SCOPES = ['day', 'week'];

/**
 * Every outcome the server can put on a job. The sheet renders all three.
 *
 * `complete` and `carry_items` BOTH finish the job. The second one ALSO puts
 * the items nobody ticked onto a new job for the next day. The word was
 * `carry` until 2026-09-11, when it meant the whole job moved instead of
 * finishing — the owner rejected that, and the token changed with the meaning
 * so nothing keeps comparing against a word that now says the opposite.
 */
export const DAY_END_OUTCOMES = ['complete', 'carry_items', 'hold'];

/** The review. A GET, because it is a read and it must stay one. */
export const dayEndPath = (scope) => `/field/day-end?scope=${encodeURIComponent(scope)}`;

/** The confirm. Nothing is written until this is sent. */
export const DAY_END_CONFIRM_PATH = '/field/day-end';

/**
 * The three groups, in the order a crew member reads them.
 *
 * `hold` is LAST and is never hidden: a job quietly missing from a review is
 * a job somebody finds still open tomorrow morning. Each hold carries the
 * server's own sentence, so the phone never has to guess at the reason.
 */
export const DAY_END_GROUPS = [
  {
    key: 'complete',
    tone: 'ok',
    title: 'Will be marked done',
    empty: 'Nothing is finished yet.',
  },
  {
    key: 'carry_items',
    tone: 'sched',
    // Both halves in one line, because both are true of every job in this
    // group and the owner asked for exactly this sentence: the job finishes,
    // the leftovers move. The day is filled in from the plan — "Also done —
    // what is left moves to Friday, Sep 11" — so the heading says WHERE, not
    // just that something is moving.
    title: 'Also done — what is left moves on',
    empty: 'Nothing is being carried over.',
  },
  {
    key: 'hold',
    tone: 'warn',
    title: 'Left alone',
    empty: 'Nothing is being left behind.',
  },
];

/** The buttons, and what each one says on a crew member's day. */
export const DAY_END_ACTIONS = [
  {
    scope: 'day',
    label: 'Finish the day',
    icon: 'check',
    variant: 'go',
    heading: 'End of day',
  },
  {
    scope: 'week',
    label: 'Finish the week',
    icon: 'calendar',
    variant: 'ghost',
    heading: 'End of week',
  },
];

export const dayEndAction = (scope) => DAY_END_ACTIONS.find((a) => a.scope === scope) || null;

/** A plan's jobs under one outcome, in the order the server sent them. */
export function jobsWithOutcome(plan, outcome) {
  if (!plan || !Array.isArray(plan.jobs)) return [];
  return plan.jobs.filter((j) => j.outcome === outcome);
}

/** Is there anything at all to confirm? Used to decide what the button says. */
export function planHasWork(plan) {
  if (!plan || !plan.counts) return false;
  return (plan.counts.complete || 0) + (plan.counts.carry_items || 0) > 0;
}

/** How many jobs this plan would close — both outcomes finish the job. */
export function jobsClosing(plan) {
  if (!plan || !plan.counts) return 0;
  return (plan.counts.complete || 0) + (plan.counts.carry_items || 0);
}

/** How many checklist items move to the next day. From the server's count. */
export function itemsMoving(plan) {
  if (!plan || !plan.counts) return 0;
  return plan.counts.items || 0;
}

/**
 * The heading over the "what is left moves on" group, with the day named.
 *
 * The owner asked for the destination in words, and a group heading that does
 * not say where is the same non-answer as a count of tasks with no task names.
 */
export function carryItemsTitle(plan) {
  const to = plan && plan.carry_to_label;
  return to ? `Also done — what is left moves to ${to}` : 'Also done — what is left moves on';
}

/**
 * What the confirm button says it will do, with the plan's own numbers on it.
 *
 * Two numbers rather than one, because they count different things and a crew
 * member acts on both: how many JOBS close, and how many ITEMS end up on
 * tomorrow. Three jobs can carry one item between them or twelve.
 */
export function confirmLabel(plan) {
  const jobs = jobsClosing(plan);
  const items = itemsMoving(plan);
  if (!items) return `Close ${jobs}`;
  return `Close ${jobs}, move ${items} item${items === 1 ? '' : 's'}`;
}

/**
 * One line summarising what confirming actually did.
 *
 * Built from the SERVER's answer (`completed` / `carried` / `skipped` /
 * `spawned`), never from what the screen expected to happen, and it names the
 * day the leftovers landed on — the same sentence after the fact as before it.
 *
 * `carried` replaced `moved` on 2026-09-11 with the model behind it: nothing
 * moves a job any more, so the count that matters is ITEMS, not jobs. Every
 * job in `carried` is already counted in `completed`, because it finished —
 * adding it again would tell a crew member they closed more jobs than they
 * have.
 */
export function appliedSentence(applied) {
  if (!applied) return null;
  const done = (applied.completed || []).length;
  const carried = applied.carried || [];
  const items = carried.reduce((n, c) => n + ((c.subtasks || []).length), 0);
  const bits = [];
  if (done) bits.push(`${done} job${done === 1 ? '' : 's'} marked done`);
  if (items) {
    const to = carried[0].to_label;
    bits.push(to
      ? `${items} item${items === 1 ? '' : 's'} moved to ${to}`
      : `${items} item${items === 1 ? '' : 's'} moved on`);
  }
  // The set rate following the work, after the fact as well as before it —
  // the review said it would happen and this is the same sentence confirming
  // it did. Counted, never named: no /api/field endpoint sends a pay figure.
  const paid = carried.filter((c) => c.pay_moved).length;
  if (paid) bits.push(paid === 1 ? 'the set rate moved with it' : `${paid} set rates moved with them`);
  if ((applied.skipped || []).length) bits.push(`${applied.skipped.length} skipped`);
  // The repeat. `applyPlan` returns `spawned` — the next occurrences it
  // created for jobs that repeat — and this sentence ignored it, so a crew
  // member closing a weekly job was told "1 job marked done" and nothing
  // about next week being on the board. The server had already done the work
  // and the only thing missing was saying so.
  //
  // Named by DAY rather than by count where they all land together, because
  // "1 back on Thursday" is the fact a crew member acts on and "1 repeat
  // created" is not. Where they differ, the count is the honest summary.
  const spawned = applied.spawned || [];
  if (spawned.length) {
    const labels = [...new Set(spawned.map((j) => j.scheduled_date_label).filter(Boolean))];
    bits.push(labels.length === 1
      ? `${spawned.length} back on ${labels[0]}`
      : `${spawned.length} scheduled again`);
  }
  if (bits.length === 0) return 'Nothing needed closing.';
  return `${bits.join(', ')}.`;
}

// ── Coming back cheap ──────────────────────────────────────────────────────
//
// The review is a LOOP: a crew member taps a job to tick the task they
// forgot, fixes it, and presses the button again. Making them find the button
// a second time is the part that would stop them bothering, so the sheet
// remembers it was open and reopens itself once when Today comes back.
//
// sessionStorage, and ONE-SHOT: it is cleared the moment it is read. A flag
// that survived would pop the sheet open on a Monday morning because somebody
// tapped a job on Friday. Every read and write is wrapped, because a phone in
// a private window throws on the accessor itself.

export const DAY_END_RESUME_KEY = 'fw_day_end_resume';

/** Remember which review to come back to, on the way out to a job. */
export function rememberReview(scope) {
  if (!DAY_END_SCOPES.includes(scope)) return;
  try { window.sessionStorage.setItem(DAY_END_RESUME_KEY, scope); } catch (_) { /* */ }
}

/** The remembered scope, or null — and it is gone once you have it. */
export function takeRemembered() {
  let value = null;
  try {
    value = window.sessionStorage.getItem(DAY_END_RESUME_KEY);
    window.sessionStorage.removeItem(DAY_END_RESUME_KEY);
  } catch (_) { return null; }
  return DAY_END_SCOPES.includes(value) ? value : null;
}

/** Drop it — the review was closed or confirmed, so there is nothing to resume. */
export function forgetReview() {
  try { window.sessionStorage.removeItem(DAY_END_RESUME_KEY); } catch (_) { /* */ }
}
